import type { FlagKind, Practice, Tool } from "@/api/types";
import { plainText } from "@/lib/plainText";
import type { Searchable } from "@/lib/search";

export { fieldHints, fieldQuery, matches } from "@/lib/search";

// The Tools page's catalog: tools and the radar's practices, browsed together. This builds
// what the search sees for each, and the Needs attention pulldown's filter.

/** One card on the Tools page: a tool, or a practice (a radar practice). */
export type CatalogEntry = { type: "tool"; item: Tool } | { type: "practice"; item: Practice };

/** Kinds of tool Guild built and runs itself; every other kind is bought or adopted. */
const GUILD_BUILT_KINDS = new Set(["internal-platform", "internal-library"]);

/** Whether Guild built a tool or it comes from outside, from its kind. Null when the kind
 * isn't set yet, so an unclassified tool is never labelled either way. */
export function toolOrigin(tool: Pick<Tool, "kind">): "Guild-built" | "Third party" | null {
  if (!tool.kind) return null;
  return GUILD_BUILT_KINDS.has(tool.kind) ? "Guild-built" : "Third party";
}

/** How each flag kind reads — the pulldown's options. Mirrors backend domain/flags.py's KINDS
 * (same order: what's broken or contradictory first, routine curation last). */
export const FLAG_LABELS: Record<FlagKind, string> = {
  broken_link: "Broken link",
  radar_trelica: "Radar and Trelica disagree",
  standard_conflict: "Standard and radar conflict",
  promotion: "Promotion candidate",
  drafted_links: "Drafted links awaiting review",
  standards_gap: "Possible standards gap",
  propose_blip: "Not on the Tech Radar",
  unreviewed: "Not yet reviewed",
};

/** The pulldown's choice: everything, anything needing attention (every flag but "not yet
 * reviewed", which is on nearly every imported entry and is its own choice), or one kind. */
export type AttentionFilter = "" | "attention" | FlagKind;

/** Whether an entry passes the pulldown. */
export function passesAttention(e: CatalogEntry, filter: AttentionFilter): boolean {
  if (filter === "") return true;
  if (filter === "attention") return e.item.flags.some((f) => f.kind !== "unreviewed");
  return e.item.flags.some((f) => f.kind === filter);
}

/** One entry as the search sees it. Its text is its name, kind, ring, status and description,
 * a tool's category and features, the standards (and a tool's capabilities) it's linked to —
 * confirmed or drafted, by name and id — and its flags. Its fields, for `field:value` words
 * and the detail pages' pills: `status`, `ring` ("unrated" when none), `kind` (a tool's
 * kind, or "practice"), `category` (a tool's: its Trelica category, or its radar quadrant),
 * `quadrant` (the radar quadrant, e.g. "Platforms / Services"), `origin` ("Guild-built" or
 * "Third party", from a tool's kind) and
 * `flag` (each flag's kind). There's no `type`: tools and practices
 * are told apart by the Standards pulldown, and the Tools page has only tools. */
export function catalogSearchable(
  e: CatalogEntry,
  standardNames: Map<string, string>,
  capabilityNames: Map<string, string>,
): Searchable {
  const std = (id: string) => `${id} ${standardNames.get(id) ?? ""}`;
  const cap = (id: string) => `${id} ${capabilityNames.get(id) ?? ""}`;
  const x = e.item;
  const kind = e.type === "tool" ? e.item.kind : "practice";
  const own =
    e.type === "tool"
      ? [
          e.item.category,
          ...e.item.features.map((f) => f.name),
          ...e.item.satisfies_standards.map(std),
          ...e.item.supports_capabilities.map(cap),
          ...e.item.drafted_capabilities.map((d) => cap(d.id)),
        ]
      : ["practice", ...e.item.related_standards.map(std)];
  const text = [
    x.name,
    kind ?? "",
    x.ring ?? "",
    x.status,
    plainText(x.what),
    ...own,
    ...x.drafted_standards.map((d) => std(d.id)),
    ...x.flags.map((f) => `${FLAG_LABELS[f.kind]} ${f.message}`),
  ]
    .join(" ")
    .toLowerCase();
  return {
    text,
    fields: {
      status: [x.status],
      ring: [x.ring ?? "unrated"],
      kind: [kind],
      category: e.type === "tool" ? [e.item.category] : [],
      quadrant: x.radar?.quadrant ? [x.radar.quadrant] : [],
      origin: e.type === "tool" && toolOrigin(e.item) ? [toolOrigin(e.item)!] : [],
      flag: x.flags.map((f) => f.kind),
    },
  };
}

/** How many entries each pulldown choice would show: "attention", and each kind of flag. */
export function attentionCounts(entries: CatalogEntry[]): Map<AttentionFilter, number> {
  const counts = new Map<AttentionFilter, number>();
  for (const e of entries) {
    for (const k of new Set(e.item.flags.map((f) => f.kind))) counts.set(k, (counts.get(k) ?? 0) + 1);
    if (passesAttention(e, "attention")) counts.set("attention", (counts.get("attention") ?? 0) + 1);
  }
  return counts;
}

/** The Needs attention choices, with counts — only the kinds of flag that occur. */
export function attentionOptions(counts: Map<AttentionFilter, number>, everything = "Everything") {
  return [
    { value: "", label: everything },
    { value: "attention", label: `Needs attention (${counts.get("attention") ?? 0})` },
    ...(Object.keys(FLAG_LABELS) as FlagKind[])
      .filter((k) => counts.get(k))
      .map((k) => ({ value: k, label: `${FLAG_LABELS[k]} (${counts.get(k)})`, group: "By kind of flag" })),
  ];
}
